import { useEffect, useEffectEvent, useState } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { first } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Button, LoadingIndicator, Typography } from '@cfa/react-components';

import useExploreSearch from '@/hooks/useExploreSearch';
import { useScrollMemory } from '@/hooks/useScrollMemory';
import { incrementResourceClicksAtom } from '@/hooks/useSearchSessionTracker';

import { formatDocumentTag } from '@/utils/formatDocumentTag';
import { pluralize } from '@/utils/language';
import { isApiError } from '@/utils/request';
import { hasNoSpecialCharacters } from '@/utils/string';
import { cn } from '@/utils/tailwind';

import DocumentHead from '@/components/document/DocumentHead';
import GenericError from '@/components/Error/GenericError';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';

import Constants from '@/constants';

import SearchbarSubheader from '../Layout/Subheader/SearchbarSubheader';

import SearchResultCard, {
  SearchResultCardProps,
} from './components/SearchResultCard';

import { searchClickFeedbackMutation } from '@/_generated/api-clients/xp';
import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { setDocumentAtom } from '@/atoms/document-persisted';

export const SearchResultsPage = () => {
  'use memo';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search).get('query') ?? '';
  const setDocument = useSetAtom(setDocumentAtom);
  const incrementResourceClicks = useSetAtom(incrementResourceClicksAtom);

  const addSearchResultFeedback = useMutation(searchClickFeedbackMutation());

  const [activeThumbsUp, setActiveThumbsUp] = useState<string[]>([]);
  const [activeThumbsDown, setActiveThumbsDown] = useState<string[]>([]);

  const { searchResultFeedback: featureFlagSearchResultFeedback } = useFlags();

  const maxResults = Constants.MAX_SEARCH_RESULTS;

  const {
    data,
    isFetching,
    isFetchingNextPage,
    error,
    hasNextPage,
    fetchNextPage,
  } = useExploreSearch(query);

  const firstPage = data?.pages[0];
  const misspellingSuggestion = data?.pages.length
    ? (first(firstPage?.spellingSuggestions) ?? '')
    : '';
  const queryId = data?.pages.length ? firstPage?.queryId : undefined;

  const searchResults = data?.pages.flatMap(_ => _?.results ?? []) ?? [];
  const totalResults = firstPage?.totalResults ?? 0;
  const numFound = Math.min(totalResults, maxResults);
  const noMoreResults =
    !hasNextPage && searchResults.length < numFound && searchResults.length > 0;

  const track = useAmplitudeTrack();

  const trackSearchResult = useEffectEvent((queryValue: string) => {
    if (track) {
      track('search_result', {
        num_results: totalResults,
        query: queryValue || 'empty',
      });
    }
  });

  useEffect(() => {
    if (queryId && query.length) {
      trackSearchResult(query);
    }
  }, [queryId, query]);

  const scrollMemoryKey = `search:${query}`;

  const [isNavigating, setIsNavigating] = useState(false);

  useScrollMemory({
    container: 'body',
    key: scrollMemoryKey,
    enabled: !isFetching && !isFetchingNextPage && !isNavigating,
  });

  const onSearchResultFeedback = ({
    resultId,
    resultQueryId,
  }: {
    resultId?: string;
    resultQueryId?: string;
  }) => {
    if (!resultQueryId || !resultId) {
      datadogRum.addError(
        new Error(
          `No ${
            !resultQueryId ? 'queryId' : 'resultId'
          } found in SearchResultsV2`,
        ),
      );
      return;
    }

    const resp = addSearchResultFeedback.mutateAsync({
      query: {
        queryId: resultQueryId,
        resultId,
      },
    });

    resp.catch((err: unknown) => {
      const errorDetails = {
        error: JSON.stringify(err),
        query,
        queryId: resultQueryId,
        resultId,
      };
      datadogRum.addError(
        new Error(
          `Error in addSearchResultFeedback: ${JSON.stringify(errorDetails)}`,
        ),
      );
    });
  };

  const goToDetailsPage = (id: string) => {
    setIsNavigating(true);
    navigate(`/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}/${id}`);
  };

  const onSearchCardClick: SearchResultCardProps['onClick'] = ({
    resultId,
    documentId,
  }) => {
    incrementResourceClicks();
    const resultIndex = searchResults.findIndex(_ => _.id === documentId);
    const doc = resultIndex !== -1 ? searchResults[resultIndex] : undefined;

    if (track) {
      const trackingPayload: {
        query: string;
        num_results: number;
        document_id: string;
        result_position?: number;
      } = {
        query: query || 'empty',
        num_results: totalResults,
        document_id: documentId,
      };

      if (resultIndex >= 0) {
        trackingPayload.result_position = resultIndex + 1;
      } else {
        datadogRum.addError(
          new Error(
            `Clicked documentId not found in searchResults: ${documentId}`,
          ),
        );
      }

      track('search_result_click', trackingPayload);
    }

    if (doc) {
      setDocument({
        document: doc,
        routedFrom: 'search',
        icon: doc.icon,
      });
    }

    onSearchResultFeedback({
      resultQueryId: queryId,
      resultId,
    });
    goToDetailsPage(documentId);
  };

  const getFeedbackText = (id?: string) => {
    const isThumbsUp = !!id && activeThumbsUp.includes(id);
    const isThumbsDown = !!id && activeThumbsDown.includes(id);
    const feedbackGiven = isThumbsUp || isThumbsDown;

    return (
      <span
        className={cn(
          feedbackGiven && isThumbsUp && 'text-feedback-success-base',
          feedbackGiven && isThumbsDown && 'text-feedback-error-base',
        )}
      >
        {feedbackGiven
          ? t('Search.submitFeedback')
          : `${t('Search.rateResult')}:`}
      </span>
    );
  };

  const onThumbsUp: SearchResultCardProps['onThumbsUp'] = (id, resultId) => {
    setActiveThumbsUp(prev => [...prev, id]);
    setActiveThumbsDown(prev => prev.filter(item => id !== item));
    onSearchResultFeedback({
      resultQueryId: queryId,
      resultId,
    });
  };

  const onThumbsDown: SearchResultCardProps['onThumbsDown'] = (
    id,
    resultId,
  ) => {
    setActiveThumbsUp(prev => prev.filter(item => id !== item));
    setActiveThumbsDown(prev => [...prev, id]);
    onSearchResultFeedback({
      resultQueryId: queryId,
      resultId,
    });
  };

  const onSearchMisspellingSuggestionClick = (suggestion: string) => {
    setIsNavigating(true);
    navigate({
      pathname: `/${Constants.ROUTE_PATH_NAMES.SEARCH_PATH_NAME}`,
      search: `query=${suggestion}`,
    });
  };

  if (isApiError(error)) {
    datadogRum.addError(error);
    return <GenericError />;
  }

  const onLoadMore = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <>
      <SearchbarSubheader />
      <div className="relative mt-8">
        <LoadingOverlay isOpen={isFetching && !(searchResults.length > 0)} />
        <DocumentHead pageTitle={query} />
        {(!!searchResults.length || misspellingSuggestion) && !isFetching ? (
          <>
            {misspellingSuggestion && (
              <span
                className="text-grayscale-gray7 mb-4 block font-normal"
                data-testid="MisspellingSuggestionWrapper"
              >
                {t('Search.showingResultsSuggestion')}
                {': '}
                <button
                  className="text-brand-navy! cursor-pointer font-bold!"
                  data-testid="MisspellingSuggestion"
                  onClick={() =>
                    onSearchMisspellingSuggestionClick(misspellingSuggestion)
                  }
                  type="button"
                >
                  {misspellingSuggestion}
                </button>
              </span>
            )}
            <Typography variant="body1">
              {numFound} {t('Search.numberOfResultsFor')} <b>{query}</b>
            </Typography>
          </>
        ) : (
          <Typography variant="body1">
            {numFound} {t('Search.numberOfResultsFor')} <b>{query}</b>
          </Typography>
        )}
        <div>
          {!searchResults.length ? (
            <div className="px-5 py-7">{t('Search.noResults')}</div>
          ) : (
            searchResults.map(searchResultDocument => {
              const {
                icon,
                id,
                contentApiUrl,
                contentUrl,
                lastModified,
                name,
                otherContentUrl,
                searchId,
                sourceSystem,
                tags,
                type,
              } = searchResultDocument;

              const isTridion =
                sourceSystem === Constants.DOCUMENT_TYPES.TRIDION.toUpperCase();
              const docUseContentUrl =
                isTridion ||
                sourceSystem ===
                  Constants.EXPANDED_FILE_FORMAT_TYPES.GAME.toUpperCase() ||
                sourceSystem ===
                  Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE.toUpperCase();
              const documentId = id ?? '';
              if (!documentId) {
                return null;
              }

              const baseUrl = docUseContentUrl
                ? (contentUrl ?? contentApiUrl ?? '')
                : (contentApiUrl ?? contentUrl ?? '');
              const translatedUrl = otherContentUrl ?? '';
              const documentUrl = {
                base: baseUrl,
                translated: translatedUrl,
              };

              const newLocationTypes = tags?.map((tag: string) =>
                formatDocumentTag(tag),
              );

              const newResultId = searchId || documentId;
              const documentType = type ?? '';
              const iconName = icon ?? '';
              const safeIcon =
                iconName && hasNoSpecialCharacters(iconName)
                  ? iconName
                  : 'placeholder';
              const modifiedDate = lastModified
                ? new Date(lastModified).toLocaleDateString()
                : '';
              const documentName = name ?? '';

              return (
                <SearchResultCard
                  documentId={documentId}
                  documentType={documentType}
                  featureFlagSearchResultFeedback={
                    featureFlagSearchResultFeedback
                  }
                  getFeedbackText={getFeedbackText}
                  icon={safeIcon}
                  id={documentId}
                  isMarketTest={false}
                  isThumbsDown={activeThumbsDown.includes(documentId)}
                  isThumbsUp={activeThumbsUp.includes(documentId)}
                  isTridion={isTridion}
                  key={documentId}
                  lastModified={modifiedDate}
                  locationTypes={newLocationTypes ?? []}
                  name={documentName}
                  onClick={onSearchCardClick}
                  onThumbsDown={onThumbsDown}
                  onThumbsUp={onThumbsUp}
                  resultId={newResultId}
                  url={documentUrl}
                />
              );
            })
          )}
        </div>
        {data && hasNextPage && !noMoreResults && (
          <Button
            className="mx-auto! mt-8! block!"
            color="secondary"
            data-testid="LoadMore"
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
            size="lg"
            variant="filled"
          >
            <>
              {isFetchingNextPage && (
                <LoadingIndicator
                  disabled
                  size="sm"
                  variant="inline"
                  className="-mt-1.5! mr-2.5!"
                />
              )}
              {t('Button.loadMore')}
            </>
          </Button>
        )}
        {!!searchResults.length && (
          <Typography
            variant="body1"
            className="text-grayscale-gray6! mt-4! text-center!"
          >
            {t('Search.showingResultsFooter', {
              numberOfResults: noMoreResults
                ? searchResults.length
                : numFound <= 100
                  ? numFound
                  : '100+',
              resultsText: pluralize(
                noMoreResults ? searchResults.length : numFound,
                t('Search.resultText'),
                t('Search.resultsText'),
              ),
              showingResults: searchResults.length,
            })}
          </Typography>
        )}
        <div className="h-[400px]" />
      </div>
    </>
  );
};

export default SearchResultsPage;
